import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { fetchInternational, fetchCountries } from "../lib/api";
import { Img } from "./NewsCard";

const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  // Convert ISO-alpha-2 → flag emoji
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

export default function InternationalSection() {
  const [country, setCountry] = useState("");
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchInternational(country || undefined), fetchCountries()])
      .then(([rows, cs]) => {
        if (!alive) return;
        setItems(rows); setCountries(cs);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [country]);

  return (
    <section className="py-10" data-testid="international-section">
      <div className="section-title">
        <span className="pip" />
        <h2><Globe size={18} className="inline mr-2 -mt-1" />Honest News · International</h2>
        <span className="ml-auto text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">
          Independent, non-state-sponsored
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          onClick={() => setCountry("")}
          className={`chip ${country === "" ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
          data-testid="country-chip-all"
        >
          🌐 All countries
        </button>
        {countries.map((c) => (
          <button
            key={c.code}
            onClick={() => setCountry(c.code)}
            className={`chip ${country === c.code ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
            data-testid={`country-chip-${c.code}`}
          >
            {FLAG(c.code)} {c.name} <span className="opacity-60 ml-1">{c.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[14px] text-[var(--muted)] italic py-6 text-center">Loading international wire…</div>
      ) : items.length === 0 ? (
        <div className="text-[14px] text-[var(--muted)] italic py-6 text-center">
          No stories yet for this country. Try All countries or refresh in a minute.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.slice(0, 8).map((a) => (
            <Link key={a.id} to={`/article/${a.id}`} className="news-card" data-testid={`intl-card-${a.id}`}>
              <div className="image">
                <Img src={a.image} category={a.category} alt={a.title} headline={a.title} source={a.source} />
              </div>
              <div className="body">
                <span className="cat">
                  {FLAG(a.country)} {a.country_label || a.country}
                </span>
                <div className="title">{a.title}</div>
                <div className="meta">
                  <span className="font-semibold text-[var(--ink)]">{a.source}</span>
                  <span>·</span>
                  <span>{new Date(a.published).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
